import { AddReactionInput, WatchMode } from '@clipr/lib';
import { JobModel, Reaction } from '../../entities';
import { AsyncResult, isFiniteNumber } from '../../core';
import { notifyError, notifySuccess } from '../../services/notifications';
import { InputPlayerReactionName } from '../playerReactions/playerReactionSchema';
import { PlayerComponentName } from './playerSchema';
import { StoreNode } from '../../store';

// ---------------------------------------------------------------------------
// Context interface — the subset of PlayerState needed by reaction helpers.
// ---------------------------------------------------------------------------

export interface ReactionsContext extends StoreNode {
  readonly job: JobModel | null;
  readonly jobId: string | null;
  readonly isEditMode: boolean;
  readonly isLiveStream: boolean;
  readonly disableReactions: boolean | null;
  readonly reactionTime: number | null;
  isComponentVisible(name: PlayerComponentName): boolean;
}

// ---------------------------------------------------------------------------
// Computed helpers
// ---------------------------------------------------------------------------

export function shouldShowReactionsButton(ctx: ReactionsContext): boolean {
  if (!ctx.isComponentVisible(PlayerComponentName.ReactionButton))
    return false;

  if (ctx.isEditMode || ctx.isLiveStream)
    return false;

  return !ctx.disableReactions;
}

// ---------------------------------------------------------------------------
// Add reaction — async helper
// ---------------------------------------------------------------------------

export async function addReaction(
  ctx: ReactionsContext,
  name: InputPlayerReactionName,
): AsyncResult<Reaction> {
  const { job, jobId, reactionTime } = ctx;

  if (!job || !jobId || !reactionTime) {
    notifyError(ctx, 'Could not add your reaction.');
    return [null, new Error('No job active')];
  }

  const time = Math.round(reactionTime);

  if (!isFiniteNumber(time)) {
    notifyError(ctx, 'Could not add your reaction.');
    return [null, new Error(`Player doesn't have a valid time.`)];
  }

  const input: AddReactionInput = {
    jobId,
    reaction: name,
    videoTime: time,
    watchMode: ctx.isLiveStream ? WatchMode.Live : WatchMode.Static,
  };

  const [reaction, err] = await job.apiAddReaction(input);

  if (err || !reaction) {
    notifyError(ctx, 'Could not add your reaction.');
    return [null, err];
  }

  if (!ctx.isLiveStream)
    notifySuccess(ctx, 'Reaction added successfully');

  return [reaction];
}
